import { ArrowRight, CalendarDays, Disc3, PenLine, Plus, Radio, Video } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandLockup } from '../components/brand-mark';
import { Button } from '../components/ui/button';
import { navigateTo } from '../lib/spa';

/** The join-card focus deep link the dashboard consumes on mount (see DashboardPage). */
const JOIN_TARGET = '/?focus=join';
/** The quick-session deep link: the dashboard runs the exact sidebar quick action. */
const CREATE_TARGET = '/?action=create';

/** The feature row of the landing: the workspace's shipped surfaces, in milestone order. */
const FEATURES = [
  { icon: Video, labelKey: 'landing.feature_meetings', textKey: 'landing.feature_meetings_text' },
  {
    icon: Radio,
    labelKey: 'landing.feature_webinars',
    textKey: 'landing.feature_webinars_text',
  },
  { icon: PenLine, labelKey: 'landing.feature_board', textKey: 'landing.feature_board_text' },
  {
    icon: CalendarDays,
    labelKey: 'landing.feature_calendar',
    textKey: 'landing.feature_calendar_text',
  },
  {
    icon: Disc3,
    labelKey: 'landing.feature_recordings',
    textKey: 'landing.feature_recordings_text',
  },
] as const;

/**
 * The Syncra landing (/welcome). The workspace dashboard owns `/` - the app's information
 * architecture folds the join form into it - so the landing hangs off /welcome and feeds the
 * existing flows: the Join CTA deep-links into the dashboard's join card (focused input), the
 * Create CTA runs the dashboard's quick-session flow (sign-in gated exactly like the sidebar
 * quick action). Assembled from the existing system only - the brand lockup, the design
 * tokens, the lucide set - no new visual language.
 */
export function LandingPage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground" data-testid="landing-page">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-5">
        <BrandLockup size="lg" />
        <a
          href="https://syncra.warsong.me/docs/"
          target="_blank"
          rel="noreferrer"
          data-testid="landing-docs-link"
          className="text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('landing.footer_docs')}
        </a>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-6 pb-16">
        <section className="mx-auto max-w-3xl text-center">
          <h1
            data-testid="landing-tagline"
            className="text-balance text-4xl font-semibold tracking-tight text-white sm:text-5xl"
          >
            {t('landing.tagline')}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-[15px] leading-relaxed text-muted-foreground">
            {t('landing.subtitle')}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              data-testid="landing-join-cta"
              className="w-full gap-2 sm:w-auto"
              onClick={() => {
                navigateTo(JOIN_TARGET);
              }}
            >
              <ArrowRight aria-hidden className="size-4" />
              {t('landing.join_cta')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              data-testid="landing-create-cta"
              className="w-full gap-2 border-white/15 bg-white/[0.04] text-white hover:bg-white/10 hover:text-white sm:w-auto"
              onClick={() => {
                navigateTo(CREATE_TARGET);
              }}
            >
              <Plus aria-hidden className="size-4" />
              {t('landing.create_cta')}
            </Button>
          </div>
        </section>

        <section className="mt-16" aria-label={t('landing.features_title')}>
          <h2 className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {t('landing.features_title')}
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {FEATURES.map((feature) => (
              <div
                key={feature.labelKey}
                data-testid="landing-feature"
                className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-4"
              >
                <feature.icon aria-hidden className="size-5 text-emerald-400" />
                <p className="mt-3 text-[13.5px] font-semibold text-white">
                  {t(feature.labelKey)}
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                  {t(feature.textKey)}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/[0.06]">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-2 px-6 py-5 text-[12.5px] text-muted-foreground sm:flex-row">
          <span>{t('landing.footer_note')}</span>
          <div className="flex items-center gap-4">
            <a
              href="https://syncra.warsong.me/docs/"
              target="_blank"
              rel="noreferrer"
              className="transition-colors hover:text-foreground"
            >
              {t('landing.footer_docs')}
            </a>
            <span aria-hidden className="text-white/20">
              |
            </span>
            <span data-testid="landing-imprint" className="cursor-default text-muted-foreground/70">
              {t('landing.footer_imprint')}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
